import http from 'utils/http'
import {set, pick, concat} from 'lodash'

import {
  ADD_ACTION_BTN,
  REMOVE_ACTION_BTN,
  SET_STEP,
  SET_NOTIFICATION_DATA_PROPERTY,
  SET_TAG_DATA_PROPERTY,
  RESET_DATA_PROPERTY,
  ADD_CUSTOM_NOTIFICATIONS,
  SET_CUSTOM_NOTIFICATION,
  SET_CUSTOM_NOTIFICATIONS,
  SET_CUSTOM_NOTIFICATIONS_PAGINATION
} from 'vx/mutations'
import initialNotificationState from 'vx/state/notification'

import initialCustomNotificationState from 'vx/state/customNotification'

// Settings state.
const state = {
  data: initialNotificationState(),
  notification: initialCustomNotificationState(),
  notifications: [],
  pagination: {},
  history: [],
  step: 1
}

// Settings state getters.
const getters = {
  /**
   * Determine if we have no more CustomNotifications to load.
   *
   * @return bool
   */
  haveNoMoreCustomNotifications: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Settings actions
const actions = {
  /**
   * Get all settings.
   *
   * @return Promise
   */
  sendNotification ({ commit }, payload) {
    return http.post(`custom_notification/send`, payload).then(response => {
      return response
    })
  },

  resetForm ({ commit }) {
    commit(RESET_DATA_PROPERTY, initialNotificationState())
  },

  addActionButton ({ commit }) {
    commit(ADD_ACTION_BTN)
  },

  removeActionButton ({ commit }, index) {
    commit(REMOVE_ACTION_BTN, index)
  },

  firstPage ({ commit }) {
    commit(SET_STEP, 1)
  },

  nextPage ({ commit }) {
    commit(SET_STEP, state.step + 1)
  },

  lastPage ({commit}) {
    commit(SET_STEP, state.step - 1)
  },

  setNotificationDataProperty ({commit}, {property, value}) {
    commit(SET_NOTIFICATION_DATA_PROPERTY, {property, value})
  },

  setTagDataProperty ({commit}, {index, value}) {
    commit(SET_TAG_DATA_PROPERTY, {index, value})
  },

  /**
   * Get a single CustomNotification.
   *
   * @return Promise
   */
  getCustomNotification ({commit}, {id}) {
    return http.get(`custom_notification/${id}`)
      .then((response) => {
        commit(SET_CUSTOM_NOTIFICATION, response.data)
        return response
      })
  },

  resetCustomNotification ({commit}) {
    commit(SET_CUSTOM_NOTIFICATION, initialCustomNotificationState())
  },

  /**
   * Get CustomNotifications.
   *
   * @return Promise
   */
  getCustomNotifications ({commit}, params = {}) {
    return http.get('custom_notification', {params})
      .then((response) => {
        commit(SET_CUSTOM_NOTIFICATIONS, response.data.data)
        commit(SET_CUSTOM_NOTIFICATIONS_PAGINATION, response.data.meta)
      })
  },

  /**
   * Get next set of paginated CustomNotifications.
   *
   * @return Promise
   */
  getNextCustomNotifications ({commit, state, getters}, params = {}) {
    if (getters.haveNoMoreCustomNotifications) {
      return Promise.reject(new Error('There are no more custom notifications to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`custom_notification`, {params})
      .then((response) => {
        commit(ADD_CUSTOM_NOTIFICATIONS, response.data.data)
        commit(SET_CUSTOM_NOTIFICATIONS_PAGINATION, response.data.meta)

        return response
      })
  }
}

// Settings state mutations.
const mutations = {
  [SET_STEP] (state, value) {
    state.step = value
  },

  [SET_NOTIFICATION_DATA_PROPERTY] (state, { property, value }) {
    set(state.data, property, value)
  },

  [SET_TAG_DATA_PROPERTY] (state, { index, value }) {
    state.data.settings.button_items[index].tags = value
  },

  [ADD_ACTION_BTN] (state) {
    if (state.data.settings.button_items.length < 3) {
      state.data.settings.button_items.push({
        button_text: '',
        tags: []
      })
    }
  },
  [REMOVE_ACTION_BTN] (state, index) {
    state.data.settings.button_items.splice(index, 1)
  },
  [RESET_DATA_PROPERTY] (state, value) {
    state.data = value
  },

  [SET_CUSTOM_NOTIFICATION] (state, notification) {
    state.notification = notification
  },

  [SET_CUSTOM_NOTIFICATIONS] (state, notifications) {
    state.notifications = notifications
  },

  [SET_CUSTOM_NOTIFICATIONS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_CUSTOM_NOTIFICATIONS] (state, notifications) {
    state.notifications = concat(state.notifications, notifications)
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
